import { callApi } from '../../../utils/apiCall';

const addDays = (date, count) =>
  new Date(Date.parse(`${date}T12:00:00Z`) + count * 86400000).toISOString().slice(0, 10);
export function earliestSearchDate(suggestion, timeZone, now = new Date()) {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return /^\d{4}-\d{2}-\d{2}$/.test(suggestion || '') && suggestion > today ? suggestion : today;
}
export function slotIdentity(slot) {
  return slot ? `${slot.start_time}:${slot.end_time}:${slot.practitioner_id}:${slot._schedule_id}` : '';
}
export function uniqueSlots(slots) {
  const unique = new Map(slots.map((slot) => [slotIdentity(slot), slot]));
  return [...unique.values()].sort(
    (a, b) =>
      Date.parse(a.start_time) - Date.parse(b.start_time) ||
      String(a.practitioner_id).localeCompare(String(b.practitioner_id))
  );
}
const awareInstant = (value) =>
  typeof value === 'string' &&
  /T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/i.test(value) &&
  Number.isFinite(Date.parse(value));

export function normalizeAvailabilityRows(rows, { practitioner, schedule, schedules, timeZone }) {
  if (!Array.isArray(rows)) throw new Error('Could not load availability. Please retry.');
  return rows.flatMap((row) =>
    (Array.isArray(row.slots) ? row.slots : [row]).map((slot) => {
      const start = slot.start_at || slot.start_time;
      const end = slot.end_at || slot.end_time || slot.finish_time;
      if (!awareInstant(start) || !awareInstant(end) || Date.parse(end) <= Date.parse(start))
        throw new Error(
          'Timezone-aware slots are unavailable for this schedule. Contact a practice manager before booking.'
        );
      const scheduleId = slot.practitioner_schedule_id || schedule.id;
      const authoritativeSchedule = schedules.find((entry) => String(entry.id) === String(scheduleId)) || schedule;
      return {
        ...slot,
        start_time: start,
        end_time: end,
        practitioner_id: practitioner.id,
        _practitioner_name: practitioner.name || practitioner.full_name || '',
        _schedule_id: scheduleId,
        _timezone: authoritativeSchedule.timezone || timeZone,
      };
    })
  );
}
export async function loadScheduleAvailability({
  practiceId,
  practitioner,
  schedule,
  schedules,
  start,
  end,
  treatmentId,
  duration,
  leadId,
  timeZone,
}) {
  const params = new URLSearchParams({
    start,
    end,
    ...(treatmentId ? { treatment: treatmentId } : { duration }),
    ...(leadId ? { lead_id: leadId } : {}),
  });
  const response = await callApi(
    `/api/v1/practices/${practiceId}/pms/practitioners/${practitioner.id}/schedules/${schedule.id}/slots/?${params}`
  );
  if (!response?.ok)
    throw new Error('Could not load availability. Please retry; no complete search result is available.');
  return normalizeAvailabilityRows(response.data?.results ?? response.data, {
    practitioner,
    schedule,
    schedules,
    timeZone,
  });
}
export async function findNextAvailable({
  practiceId,
  practitioners,
  treatmentId,
  leadId,
  start,
  horizon,
  timeZone,
  isCurrent = () => true,
  onProgress,
}) {
  if (!practiceId || !treatmentId || ![7, 14, 30].includes(Number(horizon)) || !/^\d{4}-\d{2}-\d{2}$/.test(start || ''))
    throw new Error('Choose a treatment and a valid search range.');
  const jobs = practitioners.flatMap((practitioner) =>
    practitioner.schedules.map((schedule) => ({ practitioner, schedule, schedules: practitioner.schedules }))
  );
  if (practitioners.length > 20 || jobs.length > 60)
    throw new Error(
      'Choose a dentist to narrow this search. The selected scope has too many schedules to search at once.'
    );
  if (!jobs.length) throw new Error('No schedules are available for the selected dentist.');
  let searchedThrough = start;
  for (let offset = 0; offset < Number(horizon); offset += 7) {
    if (!isCurrent()) throw new Error('Search cancelled.');
    const first = addDays(start, offset);
    const last = addDays(start, Math.min(offset + 6, Number(horizon) - 1));
    onProgress?.({ start: first, end: last });
    const candidates = [];
    // Four read requests at a time; every selected clinician participates before ranking.
    for (let index = 0; index < jobs.length; index += 4) {
      if (!isCurrent()) throw new Error('Search cancelled.');
      // Bound concurrent API reads and finish each date batch before advancing.
      // eslint-disable-next-line no-await-in-loop
      const batches = await Promise.all(
        jobs
          .slice(index, index + 4)
          .map((job) =>
            loadScheduleAvailability({ ...job, practiceId, start: first, end: last, treatmentId, leadId, timeZone })
          )
      );
      if (!isCurrent()) throw new Error('Search cancelled.');
      batches.forEach((rows) => candidates.push(...rows));
    }
    searchedThrough = last;
    const slots = uniqueSlots(candidates).filter((slot) => Date.parse(slot.start_time) > Date.now());
    if (slots.length) return { slots, searchedThrough };
  }
  return { slots: [], searchedThrough };
}
